import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PageHead, Stat } from "../../components/ui";
import { api } from "../../lib/api";
import { num } from "../../lib/format";
import { SitesSection } from "../settings/WorkSites";
import { CompanyBasicsSection } from "./company/CompanyBasics";
import { WorkPlansSection } from "./company/WorkPlans";
import { CostCentersSection } from "./company/CostCenters";

interface SetupSummary {
  units: number;
  costCenters: number;
  schedules: number;
  sites: number;
  headcount: number;
  withoutUnit: number;
  withoutSchedule: number;
}

type Tab = "basics" | "sites" | "plans" | "centers";

/** اسمُ المُعامل في العنوان — تُكتب به الروابطُ الواردة من شاشاتٍ أخرى. */
export const TAB_PARAM = "tab";

const TABS: readonly { key: Tab; label: string }[] = [
  { key: "basics", label: "البيانات" },
  { key: "sites", label: "المواقع" },
  { key: "plans", label: "خطط العمل" },
  { key: "centers", label: "مراكز التكلفة" },
] as const;

/**
 * الشركة.
 *
 * ## ولماذا ورقةٌ واحدةٌ بأربعة ألسنة
 *
 * كانت أربعَ شاشاتٍ في أربعة مواضعَ من القائمة: العنوانُ في الإعدادات،
 * والمواقعُ في مكانٍ ثالث، والورديّاتُ في رابع. ومن يُعدّ شركةً جديدةً
 * يفعل الأربعةَ في جلسةٍ واحدةٍ بترتيبٍ واحد — يكتب العنوانَ، ثمّ يضع
 * نقطةَ المقرّ، ثمّ يرسم ورديّاته ولائحةَ جزاءاتها، ثمّ يقسم تكلفتها.
 * وتفريقُها على القائمة يجعله يترك ثلاثةً منها ويظنّ أنّه انتهى.
 *
 * ## وما ليس هنا
 *
 * الهيكلُ التنظيميُّ — من يتبع من — في بابه: هو رسمٌ يُحرَّر بالسحب، لا
 * نموذجُ حقولٍ يُملأ، وحشرُه لسانًا خامسًا كان سيجعل اللسانَ الواحد أطولَ
 * من الصفحة كلِّها.
 */
export function Company() {
  /*
   * اللسانُ في العنوان لا في ذاكرة الصفحة.
   *
   * شاشاتٌ أخرى تحيل إلى لسانٍ بعينه — نموذجُ الوحدة يحيل إلى مراكز
   * التكلفة، وملفُّ الموظّف إلى خطط العمل — ورابطٌ يفتح الصفحةَ على أوّل
   * لسانٍ يترك القارئَ يبحث عمّا أُرسل إليه. وهو كذلك يجعل الرجوعَ
   * بالمتصفّح يعمل، ويجعل الرابطَ يُنسخ ويُرسل.
   */
  const [params, setParams] = useSearchParams();
  const asked = params.get(TAB_PARAM);
  const tab: Tab = TABS.some((t) => t.key === asked)
    ? (asked as Tab)
    : TABS[0].key;
  const setTab = (next: Tab) => {
    const nextParams = new URLSearchParams(params);
    if (next === TABS[0].key) nextParams.delete(TAB_PARAM);
    else nextParams.set(TAB_PARAM, next);
    setParams(nextParams, { replace: true });
  };

  const summary = useQuery({
    queryKey: ["hr", "setup", "summary"],
    queryFn: () => api<SetupSummary>("/hr/setup/summary"),
  });

  const s = summary.data;

  return (
    <div className="page">
      <PageHead
        title="الشركة"
        subtitle="ما تُعرّفه الشركةُ عن نفسها مرّةً ثمّ يُختار منه في كلّ ملفّ: عنوانُها وعملتُها، ومواقعُ حضورها، وخططُ عملها ولوائحُ جزاءاتها، ومراكزُ تكلفتها"
      />

      {s && (
        <div className="stats">
          <Stat
            label="مواقع الحضور"
            value={num(s.sites)}
            note={
              s.sites === 0
                ? "بلا موقعٍ يُقبل التسجيل من أيّ مكان"
                : "نقطةٌ ونصفُ قطرٍ لكلّ موقع"
            }
          />
          <Stat
            label="خطط العمل"
            value={num(s.schedules)}
            note={
              s.withoutSchedule > 0
                ? `و${num(s.withoutSchedule)} موظّفًا بلا خطة`
                : "كلُّ من على رأس العمل مربوطٌ بخطة"
            }
          />
          <Stat
            label="مراكز التكلفة"
            value={num(s.costCenters)}
            note="تُحمَّل عليها رواتبُ من يتبعها"
          />
          <Stat
            label="على رأس العمل"
            value={num(s.headcount)}
            note={
              s.withoutUnit > 0
                ? `منهم ${num(s.withoutUnit)} خارج الهيكل`
                : "كلُّهم داخل الهيكل"
            }
          />
        </div>
      )}

      <div className="tabs" role="tablist" aria-label="أقسام إعداد الشركة">
        {TABS.map((t) => (
          <button
            key={t.key}
            className="tab"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "basics" && <CompanyBasicsSection />}
      {tab === "sites" && <SitesSection />}
      {tab === "plans" && <WorkPlansSection />}
      {tab === "centers" && <CostCentersSection />}
    </div>
  );
}
